import { ComponentType, createContext, FC, PropsWithChildren, PureComponent, useContext, useEffect, useReducer, useRef, } from "react";
import {
  ComparingZipAnalysisQueryParameters,
  SpecialtyLevelResults,
  ZipAnalysisQueryParameters,
  ZipAnalysisSearchOptions,
  ZipAnalysisSearchParameters,
  ZipAnalysisSearchSelections,
  ZipAnalysisSortOption,
  ZipClassAggregateResultDisplayModel,
  ZipClassTableData,
  ZipCodeDisplayModel,
  ZipCodeTableData,
  ZipContextInProps,
  ZipContextOutProps,
  ZipContextState,
  ZipProjectActionsEnum,
  ZipProjectModuleRoutesEnum,
} from "./interfaces";
import { AttestationRateDays, ChangedGapsEnum, ProjectPerspective, ProjectRunConfigurationStatus, ProjectRunConfigurationType, SpecialtyTypeEnum, TimeFrequency } from "@app/projects/enumerators";
import { zipProjectReducer } from "./zipProjectReducer";
import ZipProjectService from "@app/projects/zipProjectService";
import ProjectService from "@app/projects/projectService";
import Project from "@app/projects/project";
import { SortDirections } from "@app/shared/waffles";
import { useHistory, useRouteMatch } from "react-router-dom";
import { parseQuerystring, querystringify } from "@app/shared/utility";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { ZipCodeAnalysisStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { ProjectModule } from "@app/projects/literals";
import { getTopAndBottomRunConfigurations, getSortedColumnFromEnum, haveFilterSelectionsChanged, isProjectRunning, translateUrlModuleToProjectModule } from "./utility";
import { AdequacyFilters } from "@app/projects/network-assessment/adequacy";
import { TemplateService } from "@app/templates";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { FeatureFlagContext, Features } from "@app/feature-flags";

const initialValues: ZipContextOutProps = {
  dispatch: null,
  getSearchParametersFromFilterSelections: () => null,
  onApplyFilters: null,
  onChangeChangedGapsFilter: null,
  onChangeCompareToRunConfiguration: null,
  onChangePerspectiveToZipClass: null,
  onChangePerspectiveToZipCode: null,
  onChangeProjectStatus: null,
  onChangeRunConfiguration: null,
  onChangeSelectedMemberships: null,
  onChangeTablePage: null,
  onClickSpecialtyGroupOrZipCode: null,
  onEditTimeslicePublishComment: null,
  onLoadPage: null,
  onProcessProviderChanges: null,
  onPublishTimeSlice: null,
  onResetFilters: null,
  onResetStatusBanners: null,
  onRunProject: null,
  onSortTable: null,
  onSubmitEditProject: null,
  state: {
    adequacyFilters: {
      active: true,
      all: true,
      approved: true,
      changedGaps: null,
      exceptions: false,
      gaps: false,
      opportunity: false,
      pending: true,
      specialtyType: null,
      viewBy: ProjectPerspective.ByZipCode,
    },
    attestationRateDays: AttestationRateDays.ThreeSixtyFive,
    availablePerspectives: [],
    calculationMethod: null,
    calculationMethodCompared: null,
    compareToRunConfiguration: null,
    dualMembershipRunConfiguration: null,
    error: null,
    filterOptions: {
      city: [],
      county: [],
      metOverall: [],
      metPassPercent: [],
      specialtyGroupCode: [],
      state: [],
      zipClass: [],
      zipCode: [],
    },
    filterSelections: {
      city: "",
      county: {
        display: "All Counties",
        key: "all",
        name: "All Counties",
      },
      metOverall: {
        key: "all",
        name: "All",
      },
      metPassPercent: {
        key: "all",
        name: "All",
      },
      specialtyGroupCode: {
        display: "All Specialty Groups",
        key: "all",
        name: "All Specialty Groups",
      },
      state: {
        display: "All States",
        key: "all",
        name: "All States",
      },
      zipClass: {
        display: "All Classifications",
        key: "all",
        name: "All Classifications",
      },
      zipCode: "",
    },
    hasExpandableSpecialties: false,
    isCompareMode: false,
    isDrilledIn: false,
    isDualMembershipMode: false,
    loading: true,
    // the module currently never changes because summary relies on route instead of state
    // that should probably be refactored when more modules are added to zip ui, like pci
    module: "Adequacy",
    perspective: ProjectPerspective.ByZipCode,
    processProviderChanges: () => null,
    project: null,
    projectHasUnprocessedProjectionProviders: false,
    regionSetIds: [],
    regionSetSpecialtyGroupToPartsLookup: {},
    runConfiguration: null,
    runFrequency: TimeFrequency.All,
    selectedRows: [],
    setIsDualMembershipMode: () => null,
    setProjectHasUnprocessedProjectionProviders: () => null,
    setProjectProjectionFlags: () => null,
    setShouldRefreshMarketProviders: () => null,
    setShouldRefreshNetNewProviders: () => null,
    setShouldRefreshNetworkProviders: () => null,
    setShouldRefreshProspectProviders: () => null,
    shouldRefreshMarketProviders: false,
    shouldRefreshNetNewProviders: false,
    shouldRefreshNetworkProviders: false,
    shouldRefreshProspectProviders: false,
    shouldShowSummariesTab: false,
    skip: 0,
    sortOption: {
      column: null,
      direction: SortDirections.None,
    },
    take: 50,
    templateMetadata: null,
    templateMetadataCompared: null,
    totalNumberOfRecords: 0,
    trendChartData: [],
    urlPathnameString: "",
    urlSearchString: window.location.search.substring(1),
    zipClassDrillInDetails: [],
    zipDetails: [],
    zipDetailsCompared: [],
    zipExpansions: new Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>(),
    zipExpansionsCompared: new Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>(),
  }
}

export const ZipProjectContext = createContext(initialValues);

const ZipProjectContextProvider: FC<ZipContextInProps> = (props: ZipContextInProps) => {
  /**
   * Component Setup
   */
  const [state, dispatch] = useReducer(zipProjectReducer, initialValues.state);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const history = useHistory();
  const match = useRouteMatch<{ projectId: string, module: ProjectModule }>();
  const projectService = useRef(new ProjectService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const templateService = useRef(new TemplateService()).current;
  const urlShouldReplace = useRef(false);

  /**
   * Handling side effects.
   * These are typically caused by changes to the URL that are caused by 
   * actors outside the react app, like a user updating the address directly
   * or navigating through the history list.
   */

  // if the user arrived here from clicking a network assessment dashboard tile while
  // dashboard filters were applied, we'll need to convert some /county/x/specialty/y bits
  // to our search params
  useEffect(() => {
    const partsToExtract = ["county", "state", "specialty", "zip"];
    const { updatedPathname, extractedValues } = extractUrlParts(partsToExtract);
    // county projects use /specialty/ in the url, but the zip api uses specialtyGroupCode
    // that conversion has to be made somewhere, so right now it's here
    extractedValues.specialtyGroupCode = extractedValues.specialty;
    delete extractedValues.specialty;
    const updatedUrlSearchParams = parseQuerystring(history.location.search);
    Object.entries(extractedValues).forEach(([key, value]) => {
      if (!!value) {
        updatedUrlSearchParams[key] = value;
      }
    });
    replaceHistory(updatedPathname, querystringify(updatedUrlSearchParams));
  }, []);

  // if the pathname doesn't include any module, add it
  // this assumes there's nothing else in the pathname after the project id
  useEffect(() => {
    let newPathname = `${history.location.pathname}`;
    if (!match.params.module) {
      newPathname += `/${state.module.toLowerCase()}`;
      // replace the history item here so we don't have to click back twice to return to the dashboard
      replaceHistory(newPathname, history.location.search);
    }
  }, [history.location.pathname]);

  // update the url to stay in sync with state
  useEffect(() => {
    if (!state.project?.projectId) {
      return;
    }
    if (urlShouldReplace.current) {
      replaceHistory(state.urlPathnameString, state.urlSearchString);
      urlShouldReplace.current = false;
    } else {
      pushHistory(state.urlPathnameString, state.urlSearchString);
    }
  }, [state.urlPathnameString, state.urlSearchString]);

  // if the search params are updated
  useEffect(() => {
    try {
      if (!state.project?.projectId) {
        return;
      }
      /**
       * This could have been triggered by either new filters being applied,
       * or by a user going back or forward in their history.
       * We need to check the filters to determine whether it's the second case,
       * and if it is, refresh the page data.
       */
      const urlSearch = new URLSearchParams(history.location.search);
      urlSearch.sort();
      const stateSearch = new URLSearchParams(getUrlSearchString(state.filterSelections, state.adequacyFilters));
      stateSearch.sort();
      const theyMatch = urlSearch.toString() === stateSearch.toString();

      // if the filters are different to state, it means they went through history,
      // instead of the URL being updated according to new filters
      if (!theyMatch) {
        dispatch({
          payload: {},
          type: ZipProjectActionsEnum.Loading,
        });
        const newPerspective = urlSearch.get("viewBy") as ProjectPerspective;

        getNewFilterOptions(newPerspective, state.project.projectId, state.runConfiguration, state.compareToRunConfiguration).then((newFilterOptions) => {
          // Don't need to update search params because that's why we're here already
          // Use initialValues.state.filterSelections so we don't include filters that were removed from the search params,
          // basically building a whole new set
          const { newFilterSelections } = syncFilterSelectionsToSearchParams(newFilterOptions, initialValues.state.filterSelections);
          const newAdequacyFilters = getAdequacyFiltersFromSearchParameters(parseQuerystring(history.location.search));
          Promise.all([
            getNewZipDetails(state.project, newPerspective, 0, undefined, newFilterSelections, undefined, undefined, undefined, newAdequacyFilters),
            getNewTrendChartData(newPerspective || state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections))
          ]).then(([newZipDetails, newTrendChartData]) => {
            const newIsDrilledIn = getIsDrilledIn(newZipDetails[0]?.data);
            const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetails[0]?.data);
            const newZipExpansions = formatExpansions(newZipDetails[0]?.specialtyLevelResults);
            const newZipExpansionsCompared = formatExpansions(newZipDetails[1]?.specialtyLevelResults);
            const newSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters);
            urlShouldReplace.current = true;

            dispatch({
              payload: {
                adequacyFilters: newAdequacyFilters,
                filterOptions: newFilterOptions,
                filterSelections: newFilterSelections,
                hasExpandableSpecialties: newZipDetails[0]?.hasExpandableSpecialties || newZipDetails[1]?.hasExpandableSpecialties,
                isDrilledIn: newIsDrilledIn,
                loading: false,
                module: match.params.module,
                perspective: newPerspective || state.perspective,
                regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
                shouldRefreshNetworkProviders: newIsDrilledIn,
                shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, newFilterSelections),
                totalNumberOfRecords: newZipDetails[0]?.totalNumberOfRecords,
                trendChartData: newTrendChartData,
                urlSearchString: newSearchString,
                zipDetails: newZipDetails[0]?.data,
                zipDetailsCompared: newZipDetails[1]?.data,
                zipExpansions: newZipExpansions,
                zipExpansionsCompared: newZipExpansionsCompared,
              },
              type: ZipProjectActionsEnum.NavigateHistory,
            })
          });
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }, [history.location.search]);

  /**
   * Helper methods.
   * These are simple methods that do one thing and are called from multiple other places in the file
   */
  const getProjectData = async (projectId: string) => {
    return await projectService.getById(parseInt(projectId, 10), true, false);
  }

  const getIsDrilledIn = (data: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[], perspective: ProjectPerspective = state.perspective) => {
    if (!data || data.length === 0)
      return false;

    switch (perspective) {
      case ProjectPerspective.ByZipClass:
        const { zipClass, specialtyGroupCode } = data[0];
        return data.every((d) => d.zipClass === zipClass && d.specialtyGroupCode === specialtyGroupCode);
      case ProjectPerspective.ByZipCode:
        const regionSetSpecialtyGroupId = (data[0] as ZipCodeDisplayModel)?.regionSetSpecialtyGroupId;
        return (data as ZipCodeDisplayModel[]).every((d) => d.regionSetSpecialtyGroupId === regionSetSpecialtyGroupId);
    }
  }

  // the regionSetSpecialtyGroupIdLookup is used to map rssg ids to related pieces, 
  // so when someone clicks "create report" it's easy to get those pieces and pass them 
  // to export data and then the api
  const getRegionSetSpecialtyGroupIdLookup = (zipDetails: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]) => {
    const newRegionSetSpecialtyGroupIdLookup = structuredClone(state.regionSetSpecialtyGroupToPartsLookup);
    // right now, the zip class perspective doesn't use this or anything like it, so 
    // this conditional essentially checks the perspective of the data and returns early if it's not zip code
    if ((zipDetails as ZipCodeDisplayModel[]).some((d) => !d.regionSetSpecialtyGroupId)) {
      return newRegionSetSpecialtyGroupIdLookup;
    }
    (zipDetails as ZipCodeDisplayModel[]).forEach((item) => {
      newRegionSetSpecialtyGroupIdLookup[item.regionSetSpecialtyGroupId.toString()] = {
        regionSetId: item.regionSetId,
        specialtyGroupCode: item.specialtyGroupCode.split(" - ")[0],
        zipCode: item.zipCode
      };
    })
    return newRegionSetSpecialtyGroupIdLookup;
  }

  const getZipClassAnalysisResults = async (
    projectData: Project,
    newSkip?: number,
    newSortOption?: ZipAnalysisSortOption,
    newFilterSelections?: ZipAnalysisSearchSelections,
    newRunConfiguration?: ProjectRunConfiguration,
  ) => {
    const query: ZipAnalysisQueryParameters = {
      limit: state.take,
      offset: newSkip === undefined ? state.skip : newSkip,
      searchParameters: getSearchParametersFromFilterSelections(newFilterSelections),
      sortOptions: !!newSortOption ? [newSortOption] : [initialValues.state.sortOption],
    };

    const newZipDetails = await zipProjectService.getZipClassResults(projectData.projectId, newRunConfiguration.projectRunConfigurationId, query);

    return newZipDetails;
  }

  const getZipCodeAnalysisResults = async (
    projectData: Project,
    newSkip?: number,
    newSortOption?: ZipAnalysisSortOption,
    newFilters?: ZipAnalysisSearchSelections,
    newProjectRunConfiguration?: ProjectRunConfiguration,
  ) => {
    const query: ZipAnalysisQueryParameters = {
      limit: state.take,
      offset: newSkip === undefined ? state.skip : newSkip,
      searchParameters: getSearchParametersFromFilterSelections(newFilters || state.filterSelections),
      sortOptions: newSortOption ? [newSortOption] : [state.sortOption],
    };

    return await zipProjectService.getZipCodeResults(projectData.projectId, (newProjectRunConfiguration || state.runConfiguration).projectRunConfigurationId, query);
  }

  const getZipClassAnalysisCompareModeResults = async (
    projectData: Project,
    newSkip: number,
    newSortOption: ZipAnalysisSortOption,
    newFilterSelections: ZipAnalysisSearchSelections,
    newRunConfiguration: ProjectRunConfiguration,
    newCompareToRunConfiguration: ProjectRunConfiguration,
    changedGapsFilter: ChangedGapsEnum,
  ) => {
    const query: ComparingZipAnalysisQueryParameters = {
      changedGaps: changedGapsFilter,
      getGaps: false,
      parameters: {
        limit: state.take,
        offset: newSkip === undefined ? state.skip : newSkip,
        searchParameters: getSearchParametersFromFilterSelections(newFilterSelections),
        sortOptions: !!newSortOption ? [newSortOption] : [initialValues.state.sortOption],
      },
      projectRunConfigurationId1: newRunConfiguration.projectRunConfigurationId,
      projectRunConfigurationId2: newCompareToRunConfiguration.projectRunConfigurationId,
    };

    const newZipDetails = await zipProjectService.getZipClassCompareModeResults(projectData.projectId, query);

    return newZipDetails;
  }

  const getZipCodeAnalysisCompareModeResults = async (
    projectData: Project,
    newSkip: number,
    newSortOption: ZipAnalysisSortOption,
    newFilters: ZipAnalysisSearchSelections,
    newRunConfiguration: ProjectRunConfiguration,
    newCompareToRunConfiguration: ProjectRunConfiguration,
    changedGapsFilter: ChangedGapsEnum,
  ) => {
    const query: ComparingZipAnalysisQueryParameters = {
      changedGaps: changedGapsFilter,
      getGaps: false,
      parameters: {
        limit: state.take,
        offset: newSkip === undefined ? state.skip : newSkip,
        searchParameters: getSearchParametersFromFilterSelections(newFilters || state.filterSelections),
        sortOptions: newSortOption ? [newSortOption] : [state.sortOption],
      },
      projectRunConfigurationId1: newRunConfiguration.projectRunConfigurationId,
      projectRunConfigurationId2: newCompareToRunConfiguration.projectRunConfigurationId,
    };

    const newZipDetails = await zipProjectService.getZipCodeCompareModeResults(projectData.projectId, query);

    return newZipDetails;
  }

  const getFilteredZipCodeRegionSetIds = async (perspective: ProjectPerspective, projectId: number, runConfigurationId: number, filterSelections: ZipAnalysisSearchSelections) => {
    if (perspective !== ProjectPerspective.ByZipCode) {
      return [];
    }
    const searchParameters = getSearchParametersFromFilterSelections(filterSelections);
    return await zipProjectService.getFilteredRegionSetIds(projectId, runConfigurationId, searchParameters);
  }

  const getNewZipDetails = async (
    projectData: Project,
    perspective?: ProjectPerspective,
    skip?: number,
    sortOption?: ZipAnalysisSortOption,
    filterSelections?: ZipAnalysisSearchSelections,
    runConfiguration?: ProjectRunConfiguration,
    compareToRunConfiguration?: ProjectRunConfiguration,
    exitCompareMode?: boolean,
    adequacyFilters?: AdequacyFilters,
  ) => {
    const newPerspective = perspective || state.perspective;
    // if the skip param is 0 it'd be falsey and default to the state value
    const newSkip = skip !== undefined ? skip : state.skip;
    const newSortOption = sortOption || state.sortOption;
    const newFilters = filterSelections || state.filterSelections;
    const newRunConfiguration = runConfiguration || state.runConfiguration;
    const newCompareToRunConfiguration = exitCompareMode ? undefined : compareToRunConfiguration || state.compareToRunConfiguration;
    const changedGaps = (adequacyFilters || state.adequacyFilters).changedGaps;

    if (!!newCompareToRunConfiguration) {
      switch (newPerspective) {
        case ProjectPerspective.ByZipClass:
          return await getZipClassAnalysisCompareModeResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration, newCompareToRunConfiguration, changedGaps);
        case ProjectPerspective.ByZipCode:
        default:
          return await getZipCodeAnalysisCompareModeResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration, newCompareToRunConfiguration, changedGaps);
      }
    }
    switch (newPerspective) {
      case ProjectPerspective.ByZipClass:
        return await getZipClassAnalysisResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration);
      case ProjectPerspective.ByZipCode:
      default:
        return await getZipCodeAnalysisResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration);
    }
  }

  const getNewTrendChartData = async (perspective: ProjectPerspective, project: Project, searchParameters: ZipAnalysisSearchParameters) => {
    if (!enabledFeatureFlags.has(Features.ZIP_ANALYSIS_TRENDING)) {
      return [];
    }
    try {
      switch (perspective) {
        case ProjectPerspective.ByZipClass:
          return await zipProjectService.getZipClassTrendChartData(project.resource.resourceId, searchParameters);
        case ProjectPerspective.ByZipCode:
          return await zipProjectService.getZipCodeTrendChartData(project.resource.resourceId, searchParameters);
      }
    } catch (error) {
      // there probably aren't any run configurations available
      if (error.status === 404) {
        return [];
      }
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error,
      })
    }
  }

  const getTemplateMetadata = async (runConfiguration: ProjectRunConfiguration) => {
    // no reason to re-retrieve the template metadata if we already have it
    // undefined === undefined would be true
    if (runConfiguration?.adequacyTemplateCorrelationId === state.templateMetadata?.correlationId) {
      return state.templateMetadata;
    }
    return await templateService.getTemplateById(runConfiguration.adequacyTemplateCollectionId, runConfiguration.adequacyTemplateId, false);
  }

  const getCalculationMethod = async (newRunConfiguration: ProjectRunConfiguration, newZipData: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]) => {
    if (!newZipData || newZipData.length === 0 || !newRunConfiguration) {
      return CalculationMethodEnum.Unknown;
    }
    const specialtyGroupCode = newZipData[0]?.specialtyGroupCode;
    const calculationMethod = await templateService.getCalculationMethod(newRunConfiguration.projectRunConfigurationId, specialtyGroupCode);
    return calculationMethod;
  }

  const getUrlPathname = (projectId?: number, module?: ProjectModule) => {
    const newProjectId = projectId || state.project.projectId;
    const newModule = module || state.module;
    return `/projects/${newProjectId}/${newModule.toLowerCase()}`;
  }

  // This is the search parameters that are injected into the URL
  const getUrlSearchString = (filterSelections: ZipAnalysisSearchSelections, adequacyFilters: AdequacyFilters) => {
    return querystringify({
      changedGaps: adequacyFilters.changedGaps,
      city: filterSelections.city === "" ? null : filterSelections.city,
      county: filterSelections.county.key === "all" ? null : filterSelections.county.key,
      metOverall: filterSelections.metOverall.key === "all" ? null : filterSelections.metOverall.key,
      specialtyGroupCode: filterSelections.specialtyGroupCode.key === "all" ? null : filterSelections.specialtyGroupCode.key,
      state: filterSelections.state.key === "all" ? null : filterSelections.state.key,
      viewBy: adequacyFilters.viewBy || null,
      zipClass: filterSelections.zipClass.key === "all" ? null : filterSelections.zipClass.key,
      zipCode: filterSelections.zipCode === "" ? null : filterSelections.zipCode,
    });
  }

  // and this is the SearchParameters object that is sent to the API to request new data
  const getSearchParametersFromFilterSelections = (filterSelections: ZipAnalysisSearchSelections): ZipAnalysisSearchParameters => {
    return {
      city: filterSelections.city === "" ? null : filterSelections.city,
      county: filterSelections.county.key === "all" ? null : filterSelections.county.key,
      metOverall: filterSelections.metOverall.key === "all"
        ? null
        : filterSelections.metOverall.key.toLocaleLowerCase() === "yes"
          ? true
          : false,
      metPassPercent: filterSelections.metPassPercent.key === "all"
        ? null
        : filterSelections.metPassPercent.key.toLocaleLowerCase() === "yes"
          ? true
          : false,
      specialtyGroupCode: filterSelections.specialtyGroupCode.key === "all" ? null : filterSelections.specialtyGroupCode.key,
      state: filterSelections.state.key === "all" ? null : filterSelections.state.key,
      zipClass: filterSelections.zipClass.key === "all" ? null : filterSelections.zipClass.key,
      zipCode: filterSelections.zipCode === "" ? null : filterSelections.zipCode,
    }
  };

  const getFilterSelectionsFromSearchParameters = (searchParameters: { [key: string]: string }): ZipAnalysisSearchSelections => {
    // we only call this from keys that have DropdownItem values, so it's safe to use that type constraint
    const getSelectionFromOptions = (filterKey: keyof ZipAnalysisSearchParameters, selectionKey: string) => {
      return state.filterOptions[filterKey].find((item) => (item as DropdownItem<string>).key === selectionKey) as DropdownItem<string>;
    }

    return {
      city: !searchParameters.city ? initialValues.state.filterSelections.city : searchParameters.city,
      county: !searchParameters.county ? initialValues.state.filterSelections.county : getSelectionFromOptions("county", searchParameters.county),
      metOverall: !searchParameters.metOverall ? initialValues.state.filterSelections.metOverall : getSelectionFromOptions("metOverall", searchParameters.metOverall),
      metPassPercent: !searchParameters.metPassPercent ? initialValues.state.filterSelections.metPassPercent : getSelectionFromOptions("metPassPercent", searchParameters.metPassPercent),
      specialtyGroupCode: !searchParameters.specialtyGroupCode ? initialValues.state.filterSelections.specialtyGroupCode : getSelectionFromOptions("specialtyGroupCode", searchParameters.specialtyGroupCode),
      state: !searchParameters.state ? initialValues.state.filterSelections.state : getSelectionFromOptions("state", searchParameters.state),
      zipClass: !searchParameters.zipClass ? initialValues.state.filterSelections.zipClass : getSelectionFromOptions("zipClass", searchParameters.zipClass),
      zipCode: !searchParameters.zipCode ? initialValues.state.filterSelections.zipCode : searchParameters.zipCode,
    }
  };

  const getAdequacyFiltersFromSearchParameters = (searchParameters: { [key: string]: string }): AdequacyFilters => {
    return {
      active: searchParameters.active === "true" || initialValues.state.adequacyFilters.active,
      all: searchParameters.all === "true" || initialValues.state.adequacyFilters.all,
      approved: searchParameters.approved === "true" || initialValues.state.adequacyFilters.approved,
      changedGaps: searchParameters.changedGaps as ChangedGapsEnum || initialValues.state.adequacyFilters.changedGaps,
      exceptions: searchParameters.exceptions === "true" || initialValues.state.adequacyFilters.exceptions,
      gaps: searchParameters.gaps === "true" || initialValues.state.adequacyFilters.gaps,
      opportunity: searchParameters.opportunity === "true" || initialValues.state.adequacyFilters.opportunity,
      pending: searchParameters.pending === "true" || initialValues.state.adequacyFilters.pending,
      specialtyType: searchParameters.specialtyType as SpecialtyTypeEnum || initialValues.state.adequacyFilters.specialtyType,
      viewBy: Object.values(ProjectPerspective).find((p) => p === searchParameters.viewBy),
    };
  }

  const extractUrlParts = (partKeys: string[]) => {
    let updatedPathname = history.location.pathname;
    const extractedValues: { [key: string]: string } = {};
    const urlParts = history.location.pathname.split("/");
    partKeys.forEach((key) => {
      const extractIndex = urlParts.findIndex((f) => f === key);
      if (extractIndex >= 0) {
        extractedValues[key] = urlParts[extractIndex + 1];
        urlParts.splice(extractIndex, 2);
      }
    })
    updatedPathname = urlParts.join("/");
    return { extractedValues, updatedPathname };
  }

  const getShouldShowSummariesTab = (runConfiguration: ProjectRunConfiguration, filterSelections: ZipAnalysisSearchSelections) => {
    return !!runConfiguration
      && runConfiguration.status === ProjectRunConfigurationStatus.Succeeded
      && Object.values(filterSelections).every((value) => { return value === "" || value?.key === "all"; });
  }

  const pushHistory = (pathname: string, search: string) => {
    history.push(`${pathname}?${search}`);
  }

  const replaceHistory = (pathname: string, search: string) => {
    history.replace(`${pathname}?${search}`);
  }

  const getDefaultRunConfiguration = (projectData: Project) => {
    return projectData.projectRunConfigurations?.find((prc) => (
      !prc.isProjected
      && !prc.isTermed
      && !prc.isProjectedTermed
      && (prc.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary
        || prc.projectRunConfigurationType === ProjectRunConfigurationType.Standard)
    ))
      || (projectData.projectRunConfigurations || [])[0];
  }

  const getZipCodeFilterOptions = async (projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number) => {
    return await zipProjectService.getZipCodeFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfigurationId);
  }

  const getZipClassFilterOptions = async (projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number) => {
    return await zipProjectService.getZipClassFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfigurationId);
  }

  const getNewFilterOptions = async (perspective: ProjectPerspective, projectId: number, runConfiguration: ProjectRunConfiguration, compareToRunConfiguration?: ProjectRunConfiguration) => {
    const runConfigurationId = runConfiguration?.projectRunConfigurationId;
    const isSucceeded = runConfiguration?.status === ProjectRunConfigurationStatus.Succeeded;
    switch (perspective) {
      case ProjectPerspective.ByZipClass:
        return await getZipClassFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfiguration?.projectRunConfigurationId);
      case ProjectPerspective.ByZipCode:
      default:
        return await getZipCodeFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfiguration?.projectRunConfigurationId);
    }
  }

  const setShouldRefreshNetworkProviders = (should: boolean = false) => {
    dispatch({
      payload: { shouldRefreshNetworkProviders: should, },
      type: ZipProjectActionsEnum.RenderedNetworkProviders,
    });
  };

  const syncFilterSelectionsToSearchParams = (filterOptions: ZipAnalysisSearchOptions, filterSelections?: ZipAnalysisSearchSelections) => {
    const newUrlSearchParams = parseQuerystring(history.location.search);
    const newFilterSelections = structuredClone(filterSelections || state.filterSelections);
    // we take the search params - regardless of whether they're new - and generate
    // the dropdown filter selections based on those
    // This happens whenever the search params change to keep everything in sync
    const badKeys: string[] = [];
    Object.entries(newUrlSearchParams).forEach(([key, value]) => {
      const matchingFilterOption =
        filterOptions[key as keyof ZipAnalysisSearchOptions]?.find((option) => {
          return (option as DropdownItem<string>)?.key === value || option === value;
        });
      if (!!matchingFilterOption) {
        newFilterSelections[key as keyof ZipAnalysisSearchSelections] = matchingFilterOption as DropdownItem<string> & string;
      } else if (key === "zipCode" || key === "city") {
        newFilterSelections[key as keyof ZipAnalysisSearchSelections] = value as DropdownItem<string> & string;
      } else if (!Object.keys(state.adequacyFilters).includes(key)) {
        newFilterSelections[key as keyof ZipAnalysisSearchSelections] = initialValues.state.filterSelections[key as keyof ZipAnalysisSearchSelections] as DropdownItem<string> & string;
        badKeys.push(key);
      }
    });

    // and any query parameters that didn't match the filters get removed
    badKeys.forEach((key) => {
      delete newUrlSearchParams[key];
    })

    return { newFilterSelections, newUrlSearchParams };
  }

  const formatExpansions = (specialtyLevelResults: SpecialtyLevelResults) => {
    const newMap = new Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>();
    if (!specialtyLevelResults) {
      return newMap;
    }
    Object.keys(specialtyLevelResults).forEach((key) => {
      newMap.set(key, specialtyLevelResults[key]);
    });
    return newMap;
  }

  /**
   * Event handlers
   * These are called from components (typically as responses to user actions)
   * and do some middleware work before feeding the action into the reducer
   */

  const onRunProject = async (projectId: number) => {
    try {
      if (!!state.project && !state.project.isRunning) {
        await zipProjectService.runZipProject(projectId);
        const newFilterSelections = initialValues.state.filterSelections;
        const newAdequacyFilters = initialValues.state.adequacyFilters;
        const newDomain = getUrlPathname(state.project.projectId, state.module);
        const newUrlSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters);

        dispatch({
          payload: {
            filterSelections: initialValues.state.filterSelections,
            isDrilledIn: false,
            project: {
              ...state.project,
              isRunning: true,
            },
            regionSetIds: initialValues.state.regionSetIds,
            regionSetSpecialtyGroupToPartsLookup: {},
            selectedRows: [],
            skip: 0,
            sortOption: {
              column: null,
              direction: SortDirections.None,
            },
            urlPathnameString: newDomain,
            urlSearchString: newUrlSearchString,
            zipDetails: [],
            zipExpansions: initialValues.state.zipExpansions,
          },
          type: ZipProjectActionsEnum.StartRunProject,
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onSubmitEditProject = async (projectId: number) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const projectData = await getProjectData(projectId.toString());
      // we need to request updated project data for the project change reasons and possible column changes
      const newData = await getNewZipDetails(projectData);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      const newRunConfiguration = getDefaultRunConfiguration(projectData);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newZipExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);

      dispatch({
        payload: {
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          loading: false,
          project: projectData,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          runConfiguration: newRunConfiguration,
          templateMetadata: newTemplateMetadata,
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.EditProject,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeChangedGapsFilter = async (newChangedGapsFilter: ChangedGapsEnum) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;
      const newAdequacyFilters = structuredClone(state.adequacyFilters);
      newAdequacyFilters.changedGaps = newChangedGapsFilter;

      const newData = await getNewZipDetails(state.project, undefined, newSkip, newSort, undefined, undefined, undefined, false, newAdequacyFilters);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      const newSearchParameterString = getUrlSearchString(state.filterSelections, newAdequacyFilters);
      const newZipExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);

      dispatch({
        payload: {
          adequacyFilters: newAdequacyFilters,
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          isDrilledIn: getIsDrilledIn(newData[0]?.data || newData[1]?.data),
          loading: false,
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          selectedRows: initialValues.state.selectedRows,
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          urlSearchString: newSearchParameterString,
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeChangedGapsFilter,
      })
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  /** Note on compare mode:
   * The runConfiguration and compareToRunConfiguration in state reflect the selections in the user's dropdowns and trend chart.
   * That is not necessarily the order in which the data is displayed. 
   * The API automatically returns the older configuration's data set as item 0, and the newer configuration's data set as item 1.
   * That means, depending on the user's selections, either object in state could be item 0 or item 1. If the user changes the compareToRunConfiguration
   * from a run newer than the runConfiguration to one older than the runConfiguration, all of our zipDetails, zipDetailsCompared, zipExpansions, etc. get flipped,
   * so we can't assume anything is staying the same.
   */
  const onChangeCompareToRunConfiguration = async (newCompareToRunConfigurationId: number) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;

      const newCompareToRunConfiguration = state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === newCompareToRunConfigurationId);

      if (!!newCompareToRunConfiguration) {
        const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, newCompareToRunConfiguration, true, false);

        const newFilterOptions = await getNewFilterOptions(state.perspective, state.project.projectId, state.runConfiguration, newCompareToRunConfiguration);
        // city and zipCode are just strings that don't need a matching key
        // the rest are DropdownItem<T>s 
        const newFilters: ZipAnalysisSearchSelections = {
          city: state.filterSelections.city,
          county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
          metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
          metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
          specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
          state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
          zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
          zipCode: state.filterSelections.zipCode,
        };
        let newTrendChartData = state.trendChartData;
        if (haveFilterSelectionsChanged(state.filterSelections, newFilters)) {
          newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilters));
        }
        const newData = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilters, state.runConfiguration, newCompareToRunConfiguration);
        const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
        const newSearchParameterString = getUrlSearchString(newFilters, state.adequacyFilters);
        const newTemplateMetadataCompared = await getTemplateMetadata(newCompareToRunConfiguration);

        const newCalculationMethod = await getCalculationMethod(topConfig, newData[0]?.data);
        const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newData[1]?.data);
        const newZipExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
        const newZipExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);

        dispatch({
          payload: {
            calculationMethod: newCalculationMethod,
            calculationMethodCompared: newCalculationMethodCompared,
            compareToRunConfiguration: newCompareToRunConfiguration,
            filterOptions: newFilterOptions,
            filterSelections: newFilters,
            hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
            isCompareMode: true,
            isDrilledIn: getIsDrilledIn(newData[0]?.data || newData[1]?.data),
            loading: false,
            regionSetIds: initialValues.state.regionSetIds,
            regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
            selectedRows: initialValues.state.selectedRows,
            shouldShowSummariesTab: getShouldShowSummariesTab(newCompareToRunConfiguration, newFilters),
            skip: newSkip,
            sortOption: newSort,
            templateMetadataCompared: newTemplateMetadataCompared,
            totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
            trendChartData: newTrendChartData,
            urlSearchString: newSearchParameterString,
            zipDetails: newData[0]?.data,
            zipDetailsCompared: newData[1]?.data,
            zipExpansions: newZipExpansions,
            zipExpansionsCompared: newZipExpansionsCompared,
          },
          type: ZipProjectActionsEnum.ChangeCompareToProjectRunConfiguration
        });
      } else {
        // revert to non-compare mode
        const newFilterOptions = await getNewFilterOptions(state.perspective, state.project.projectId, state.runConfiguration);
        // city and zipCode are just strings that don't need a matching key
        // the rest are DropdownItem<T>s 
        const newFilters: ZipAnalysisSearchSelections = {
          city: state.filterSelections.city,
          county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
          metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
          metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
          specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
          state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
          zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
          zipCode: state.filterSelections.zipCode,
        };
        let newTrendChartData = state.trendChartData;
        if (haveFilterSelectionsChanged(state.filterSelections, newFilters)) {
          newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilters));
        }
        const newData = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilters, state.runConfiguration, undefined, true);
        const newSearchParameterString = getUrlSearchString(newFilters, state.adequacyFilters);
        const newCalculationMethod = await getCalculationMethod(state.runConfiguration, newData[0]?.data);
        const newZipExpansions = formatExpansions(newData[0]?.specialtyLevelResults);

        dispatch({
          payload: {
            calculationMethod: newCalculationMethod,
            calculationMethodCompared: initialValues.state.calculationMethodCompared,
            compareToRunConfiguration: initialValues.state.compareToRunConfiguration,
            filterOptions: newFilterOptions,
            filterSelections: newFilters,
            hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties,
            isCompareMode: false,
            isDrilledIn: getIsDrilledIn(newData[0]?.data),
            loading: false,
            regionSetIds: initialValues.state.regionSetIds,
            regionSetSpecialtyGroupToPartsLookup: state.regionSetSpecialtyGroupToPartsLookup,
            selectedRows: initialValues.state.selectedRows,
            shouldShowSummariesTab: getShouldShowSummariesTab(newCompareToRunConfiguration, newFilters),
            skip: newSkip,
            sortOption: newSort,
            templateMetadataCompared: initialValues.state.templateMetadataCompared,
            totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
            trendChartData: newTrendChartData,
            urlSearchString: newSearchParameterString,
            zipDetails: newData[0]?.data,
            zipExpansions: newZipExpansions,
            zipExpansionsCompared: initialValues.state.zipExpansionsCompared,
          },
          type: ZipProjectActionsEnum.ChangeCompareToProjectRunConfiguration
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangePerspectiveToZipClass = async (project?: Project, runConfiguration?: ProjectRunConfiguration) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });
      const newPerspective = ProjectPerspective.ByZipClass;
      const newProject = project || state.project;
      const newRunConfiguration = runConfiguration || state.runConfiguration;
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);

      const isInitialLoad = !!project && !!runConfiguration;
      const newIsDualMembershipMode = state.isDualMembershipMode || (isInitialLoad && !!newRunConfiguration.companionProjectRunConfigurationId);
      const newDualMembershipRunConfiguration = newProject.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId);

      let newModule = state.module;
      if (match.params.module && match.params.module === "Adequacy".toLowerCase() || match.params.module === "Summary".toLowerCase()) {
        newModule = translateUrlModuleToProjectModule(match.params.module as ZipProjectModuleRoutesEnum);
      }

      const newFilterOptions = await getNewFilterOptions(ProjectPerspective.ByZipClass, newProject.projectId, newRunConfiguration, state.compareToRunConfiguration);
      const searchParameters = parseQuerystring(history.location.search);
      const existingFilterSelections = getFilterSelectionsFromSearchParameters(searchParameters);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);

      let newFilterSelections;
      if (isInitialLoad) {
        newFilterSelections = syncFilterSelectionsToSearchParams(newFilterOptions, existingFilterSelections).newFilterSelections;
      } else {
        newFilterSelections = initialValues.state.filterSelections;
      }

      const newZipDetails = await getNewZipDetails(newProject, ProjectPerspective.ByZipClass, 0, undefined, newFilterSelections, newRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetails[0]?.data);
      const newCalculationMethodCompared = !!bottomConfig ? await getCalculationMethod(bottomConfig, newZipDetails[1]?.data) : initialValues.state.calculationMethodCompared;
      const newZipExpansions = formatExpansions(newZipDetails[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetails[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(ProjectPerspective.ByZipClass, newProject, getSearchParametersFromFilterSelections(newFilterSelections));

      const newIsDrilledIn = getIsDrilledIn(newZipDetails[0]?.data, newPerspective);
      const newPathname = getUrlPathname(newProject.projectId, newModule);

      const newAdequacyFilters = {
        ...state.adequacyFilters,
        viewBy: ProjectPerspective.ByZipClass,
      };

      const newUrlSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters);
      urlShouldReplace.current = !!project && !!runConfiguration;

      const payload: Partial<ZipContextState> = {
        adequacyFilters: newAdequacyFilters,
        calculationMethod: newCalculationMethod,
        calculationMethodCompared: newCalculationMethodCompared,
        dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
        filterOptions: newFilterOptions,
        filterSelections: newFilterSelections,
        hasExpandableSpecialties: newZipDetails[0]?.hasExpandableSpecialties || newZipDetails[1]?.hasExpandableSpecialties,
        isDrilledIn: newIsDrilledIn,
        isDualMembershipMode: newIsDualMembershipMode,
        loading: false,
        module: "Adequacy",
        perspective: newPerspective,
        project: newProject,
        regionSetIds: initialValues.state.regionSetIds,
        // something like this will be used for zip class, but not rssg specifically. tbd.
        // regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
        regionSetSpecialtyGroupToPartsLookup: state.regionSetSpecialtyGroupToPartsLookup,
        runConfiguration: newRunConfiguration,
        selectedRows: [],
        setShouldRefreshNetworkProviders: () => null,
        shouldShowSummariesTab: getShouldShowSummariesTab(newRunConfiguration, newFilterSelections),
        skip: 0,
        sortOption: initialValues.state.sortOption,
        templateMetadata: newTemplateMetadata,
        totalNumberOfRecords: newZipDetails[0]?.totalNumberOfRecords,
        trendChartData: newTrendChartData,
        urlPathnameString: newPathname,
        urlSearchString: newUrlSearchString,
        zipDetails: newZipDetails[0]?.data,
        zipDetailsCompared: newZipDetails[1]?.data,
        zipExpansions: newZipExpansions,
        zipExpansionsCompared: newZipExpansionsCompared,
      };

      dispatch({
        payload,
        type: ZipProjectActionsEnum.ChangePerspectiveZipClass,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangePerspectiveToZipCode = async (project?: Project, runConfiguration?: ProjectRunConfiguration) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const newPerspective = ProjectPerspective.ByZipCode;
      const newProject = project || state.project;
      const newRunConfiguration = runConfiguration || state.runConfiguration;
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const isInitialLoad = !!project && !!runConfiguration;
      const newIsDualMembershipMode = state.isDualMembershipMode || (isInitialLoad && !!newRunConfiguration.companionProjectRunConfigurationId);
      const newDualMembershipRunConfiguration = newProject.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId);

      let newModule = state.module;
      if (match.params.module && match.params.module === "Adequacy".toLowerCase() || match.params.module === "Summary".toLowerCase()) {
        newModule = translateUrlModuleToProjectModule(match.params.module as ZipProjectModuleRoutesEnum);
      }

      const searchParameters = parseQuerystring(history.location.search);
      const newFilterOptions = await getNewFilterOptions(ProjectPerspective.ByZipCode, newProject.projectId, newRunConfiguration, state.compareToRunConfiguration);
      const existingFilterSelections = getFilterSelectionsFromSearchParameters(searchParameters);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);

      let newFilterSelections;
      if (isInitialLoad) {
        newFilterSelections = syncFilterSelectionsToSearchParams(newFilterOptions, existingFilterSelections).newFilterSelections;
      } else {
        newFilterSelections = initialValues.state.filterSelections;
      }

      const filteredRegionSetIdsPromise = getFilteredZipCodeRegionSetIds(ProjectPerspective.ByZipCode, newProject.projectId, newRunConfiguration.projectRunConfigurationId, newFilterSelections);
      const newZipDetails = await getNewZipDetails(newProject, ProjectPerspective.ByZipCode, 0, undefined, newFilterSelections, newRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetails[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetails[1]?.data);
      const newIsDrilledIn = getIsDrilledIn(newZipDetails[0]?.data, newPerspective);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetails[0]?.data);
      const newZipExpansions = formatExpansions(newZipDetails[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetails[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(ProjectPerspective.ByZipCode, newProject, getSearchParametersFromFilterSelections(newFilterSelections));
      const newAdequacyFilters = {
        ...state.adequacyFilters,
        viewBy: ProjectPerspective.ByZipCode,
      };

      const newPathname = getUrlPathname(newProject.projectId, newModule);
      const newUrlSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters);
      urlShouldReplace.current = isInitialLoad;

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = newFilterOptions.zipClass.find((zc) => zc.key === newZipDetails[0]?.data[0].zipClass);
      const newZipClassDrillInData = newIsDrilledIn && state.perspective === ProjectPerspective.ByZipCode
        ? await getZipClassAnalysisResults(newProject, 0, undefined, classificationFilter, newRunConfiguration)
        : null;

      const payload: Partial<ZipContextState> = {
        adequacyFilters: newAdequacyFilters,
        calculationMethod: newCalculationMethod,
        calculationMethodCompared: newCalculationMethodCompared,
        dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
        filterOptions: newFilterOptions,
        filterSelections: newFilterSelections,
        hasExpandableSpecialties: newZipDetails[0]?.hasExpandableSpecialties || newZipDetails[1]?.hasExpandableSpecialties,
        isDrilledIn: newIsDrilledIn,
        isDualMembershipMode: newIsDualMembershipMode,
        loading: false,
        module: "Adequacy",
        perspective: ProjectPerspective.ByZipCode,
        project: newProject,
        regionSetIds: await filteredRegionSetIdsPromise,
        regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
        runConfiguration: newRunConfiguration,
        selectedRows: [],
        setShouldRefreshNetworkProviders: setShouldRefreshNetworkProviders,
        shouldShowSummariesTab: getShouldShowSummariesTab(newRunConfiguration, newFilterSelections),
        skip: 0,
        sortOption: initialValues.state.sortOption,
        templateMetadata: newTemplateMetadata,
        totalNumberOfRecords: newZipDetails[0]?.totalNumberOfRecords,
        trendChartData: newTrendChartData,
        urlPathnameString: newPathname,
        urlSearchString: newUrlSearchString,
        zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
        zipDetails: newZipDetails[0]?.data,
        zipDetailsCompared: newZipDetails[1]?.data,
        zipExpansions: newZipExpansions,
        zipExpansionsCompared: newZipExpansionsCompared,
      };

      dispatch({
        payload,
        type: ZipProjectActionsEnum.ChangePerspectiveZipCode,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, ...initialValues.state },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeProjectStatus = async (messagePayload: ZipCodeAnalysisStatusChangeNotificationMessagePayload) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const newProject = await getProjectData(messagePayload.projectId.toString());
      const newRunConfiguration = newProject.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === messagePayload.projectRunConfigurationId) || getDefaultRunConfiguration(newProject);
      const newFilterOptions = await getNewFilterOptions(state.perspective, newProject.projectId, newRunConfiguration, state.compareToRunConfiguration);
      const newZipDetails = await getNewZipDetails(newProject, undefined, 0, initialValues.state.sortOption, initialValues.state.filterSelections, newRunConfiguration);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetails[0]?.data);
      const newFilterSelections = initialValues.state.filterSelections;
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newZipExpansions = formatExpansions(newZipDetails[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetails[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(state.perspective, newProject, getSearchParametersFromFilterSelections(newFilterSelections));

      dispatch({
        payload: {
          filterOptions: newFilterOptions,
          // reset filters to default in case the service area or specialties changed
          filterSelections: newFilterSelections,
          hasExpandableSpecialties: newZipDetails[0]?.hasExpandableSpecialties || newZipDetails[1]?.hasExpandableSpecialties,
          isDrilledIn: false,
          loading: false,
          project: {
            ...newProject,
            isRunning: isProjectRunning(newProject),
          },
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          runConfiguration: newRunConfiguration,
          selectedRows: initialValues.state.selectedRows,
          skip: initialValues.state.skip,
          templateMetadata: newTemplateMetadata,
          totalNumberOfRecords: newZipDetails[0]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: getUrlSearchString(newFilterSelections, state.adequacyFilters),
          zipDetails: newZipDetails[0]?.data,
          zipDetailsCompared: newZipDetails[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeProjectStatus,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeRunConfiguration = async (newRunConfigurationId: number) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;

      let newRunConfiguration = state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === newRunConfigurationId);
      // the trend chart and dropdown only send primary run config ids, so if we're currently viewing the secondary membership,
      // we want to stay in the secondary membership. if we're in dual membership, this is irrelevant and will be handled farther below
      // however, if we're currently viewing the secondary membership and the new selection does not have a secondary membership, we'll stick with the primary
      if (!state.isDualMembershipMode && state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
        newRunConfiguration = state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId) || newRunConfiguration;
      }
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newFilterOptions = await getNewFilterOptions(state.perspective, state.project.projectId, newRunConfiguration, state.compareToRunConfiguration);

      // city and zipCode are just strings that don't need a matching key
      // the rest are DropdownItem<T>s 
      const newFilters: ZipAnalysisSearchSelections = {
        city: state.filterSelections.city,
        county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
        metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
        metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
        specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
        state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
        zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
        zipCode: state.filterSelections.zipCode,
      };

      // if the newFilters are different from the current selections, we need to update the trend chart items
      let newTrendChartData = state.trendChartData;
      if (haveFilterSelectionsChanged(state.filterSelections, newFilters)) {
        newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilters));
      }

      const newData = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilters, newRunConfiguration, state.compareToRunConfiguration);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      const newSearchParameterString = getUrlSearchString(newFilters, state.adequacyFilters);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(topConfig, newData[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newData[1]?.data);
      const newZipExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);

      // if the new configuration is not dual membership, we need to leave dual membership mode
      const newIsDualMembershipMode = state.isDualMembershipMode && !!newRunConfiguration.companionProjectRunConfigurationId;
      // and if it is dual membership, we need to update the secondary config
      const newDualMembershipRunConfiguration = newIsDualMembershipMode ? state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId) : null;

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
          filterOptions: newFilterOptions,
          filterSelections: newFilters,
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          isDrilledIn: getIsDrilledIn(newData[0]?.data),
          isDualMembershipMode: newIsDualMembershipMode,
          loading: false,
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          runConfiguration: newRunConfiguration,
          selectedRows: initialValues.state.selectedRows,
          shouldShowSummariesTab: getShouldShowSummariesTab(newRunConfiguration, newFilters),
          skip: newSkip,
          sortOption: newSort,
          templateMetadata: newTemplateMetadata,
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: newSearchParameterString,
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeProjectRunConfiguration
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeSelectedMemberships = (newMemberships: DropdownItem<string>[]) => {
    dispatch({
      payload: {},
      type: ZipProjectActionsEnum.Loading
    });

    // checkbox dropdown returns keys in the order selected, not the order listed
    // so if a user selected the secondary and THEN the primary, we need to make sure the primary is saved as the primary
    let newRunConfiguration = state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === parseInt(newMemberships[0].key, 10));
    if (newMemberships.length > 1 && newRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
      newRunConfiguration = state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId);
    }
    const newDualMembershipRunConfiguration = newMemberships.length > 1 ? state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId) : null;
    const newIsDualMembershipMode = !!newDualMembershipRunConfiguration;

    dispatch({
      payload: {
        compareToRunConfiguration: initialValues.state.compareToRunConfiguration,
        dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
        isCompareMode: false,
        isDualMembershipMode: newIsDualMembershipMode,
        loading: false,
        runConfiguration: newRunConfiguration,
      },
      type: ZipProjectActionsEnum.ChangeMemberships,
    })
  }

  const onLoadPage = async (projectId: string) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const newProject = await getProjectData(projectId);
      const newRunConfiguration = getDefaultRunConfiguration(newProject);
      const searchParameters = parseQuerystring(history.location.search);
      if (!!newRunConfiguration) {
        switch (searchParameters.viewBy) {
          case ProjectPerspective.ByZipClass:
            onChangePerspectiveToZipClass(newProject, newRunConfiguration);
            break;
          case ProjectPerspective.ByZipCode:
          default:
            onChangePerspectiveToZipCode(newProject, newRunConfiguration);
            break;
        }
      } else {
        dispatch({
          payload: {
            loading: false,
            project: newProject,
          },
          type: ZipProjectActionsEnum.LoadProjectWithoutRunConfiguration,
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  };

  const onPublishTimeSlice = async (comment?: string) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });
      await zipProjectService.publishZipProject(state.project.projectId, comment);

      const newProject = await getProjectData(state.project.projectId.toString());
      const newRunConfiguration = getDefaultRunConfiguration(newProject);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newTrendChartData = await getNewTrendChartData(state.perspective, newProject, getSearchParametersFromFilterSelections(state.filterSelections));

      dispatch({
        payload: {
          loading: false,
          project: newProject,
          runConfiguration: newRunConfiguration,
          templateMetadata: newTemplateMetadata,
          trendChartData: newTrendChartData,
        },
        type: ZipProjectActionsEnum.PublishProjectRunConfiguration,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onApplyFilters = async () => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const filteredRegionSetIds = await getFilteredZipCodeRegionSetIds(state.perspective, state.project.projectId, state.runConfiguration.projectRunConfigurationId, state.filterSelections);
      const newData = await getNewZipDetails(state.project, undefined, 0, undefined, state.filterSelections);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      const newExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(state.filterSelections));

      const search = parseQuerystring(history.location.search);
      Object.keys(state.filterSelections).forEach((sf) => {
        const filter = state.filterSelections[sf as keyof ZipAnalysisSearchSelections];
        const filterValue = (typeof filter === "string") ? filter : (filter as DropdownItem<string>)?.key;
        if (!!filterValue && filterValue !== "all" && filterValue !== "") {
          search[sf] = filterValue;
        } else {
          delete search[sf];
        }
      });
      const newIsDrilledIn = getIsDrilledIn(newData[0]?.data);

      const classificationFilter = structuredClone(state.filterSelections);
      classificationFilter.zipClass = state.filterOptions.zipClass.find((zc) => zc.key === newData[0]?.data[0].zipClass);
      const newZipClassDrillInData = newIsDrilledIn && state.perspective === ProjectPerspective.ByZipCode
        ? await getZipClassAnalysisResults(state.project, 0, initialValues.state.sortOption, classificationFilter, state.runConfiguration)
        : null;

      dispatch({
        payload: {
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetIds: filteredRegionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          selectedRows: [],
          shouldRefreshNetworkProviders: newIsDrilledIn,
          shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, state.filterSelections),
          skip: 0,
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: querystringify(search),
          zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ApplyFilters,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onResetFilters = async () => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const newFilterSelections = initialValues.state.filterSelections;
      const newData = await getNewZipDetails(state.project, undefined, 0, undefined, newFilterSelections);
      const newExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections));

      const search = parseQuerystring(history.location.search);
      Object.keys(newFilterSelections).forEach((sf) => {
        const filter = newFilterSelections[sf as keyof ZipAnalysisSearchSelections];
        const filterValue = (typeof filter === "string") ? filter : (filter as DropdownItem<string>)?.key;
        if (!!filterValue && filterValue !== "all" && filterValue !== "") {
          search[sf] = filterValue;
        } else {
          delete search[sf];
        }
      });
      const newIsDrilledIn = getIsDrilledIn(newData[0]?.data);

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = state.filterOptions.zipClass.find((zc) => zc.key === newData[0]?.data[0].zipClass);
      const newZipClassDrillInData = newIsDrilledIn && state.perspective === ProjectPerspective.ByZipCode
        ? await getZipClassAnalysisResults(state.project, 0, initialValues.state.sortOption, classificationFilter, state.runConfiguration)
        : null;

      dispatch({
        payload: {
          filterSelections: newFilterSelections,
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          selectedRows: [],
          shouldRefreshNetworkProviders: newIsDrilledIn,
          shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, newFilterSelections),
          skip: 0,
          sortOption: {
            column: null,
            direction: SortDirections.None,
          },
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: querystringify(search),
          zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ResetFilters,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeTablePage = async (newSkip: number) => {
    try {
      const newData = await getNewZipDetails(state.project, undefined, newSkip);
      const newExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      dispatch({
        payload: {
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          skip: newSkip,
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangePage,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  };

  const onSortTable = async (newColumn: keyof ZipCodeTableData | keyof ZipClassTableData, newDirection: SortDirections) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const sortedColumn = getSortedColumnFromEnum(newColumn, state.perspective);
      const newSortOption: ZipAnalysisSortOption = {
        column: sortedColumn,
        direction: newDirection,
      };
      const newData = await getNewZipDetails(state.project, undefined, 0, newSortOption);
      const newExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      dispatch({
        payload: {
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          loading: false,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          skip: 0,
          sortOption: newSortOption,
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.SortTable,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  };

  const onClickSpecialtyGroupOrZipCode = async (zipCode: string, specialtyGroupCode: string) => {
    try {
      dispatch({
        payload: {},
        type: ZipProjectActionsEnum.Loading,
      });

      const newFilterSelections = structuredClone(state.filterSelections);
      const matchingSpecialtyItem = state.filterOptions.specialtyGroupCode.find((sgc) => sgc.key === specialtyGroupCode)

      newFilterSelections.specialtyGroupCode = matchingSpecialtyItem;
      newFilterSelections.zipCode = zipCode;

      const filteredRegionSetIdsPromise = getFilteredZipCodeRegionSetIds(state.perspective, state.project.projectId, state.runConfiguration.projectRunConfigurationId, newFilterSelections);
      const newData = await getNewZipDetails(state.project, undefined, 0, initialValues.state.sortOption, newFilterSelections);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newData[0]?.data);
      const newSearchString = getUrlSearchString(newFilterSelections, state.adequacyFilters);
      const newShouldShowSummariesTab = getShouldShowSummariesTab(state.runConfiguration, newFilterSelections)
      const newIsDrilledIn = getIsDrilledIn(newData[0]?.data);
      const newExpansions = formatExpansions(newData[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newData[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections));

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = state.filterOptions.zipClass.find((zc) => zc.key === newData[0]?.data[0].zipClass);
      const newZipClassDrillInData = await getZipClassAnalysisResults(state.project, 0, initialValues.state.sortOption, classificationFilter, state.runConfiguration);

      dispatch({
        payload: {
          filterSelections: newFilterSelections,
          hasExpandableSpecialties: newData[0]?.hasExpandableSpecialties || newData[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetIds: await filteredRegionSetIdsPromise,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          selectedRows: [],
          shouldRefreshNetworkProviders: false,
          shouldShowSummariesTab: newShouldShowSummariesTab,
          skip: 0,
          sortOption: {
            column: null,
            direction: SortDirections.None,
          },
          totalNumberOfRecords: newData[0]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: newSearchString,
          zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
          zipDetails: newData[0]?.data,
          zipDetailsCompared: newData[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ClickSpecialtyGroupOrZipCode,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onEditTimeslicePublishComment = async () => {
    try {
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(state.filterSelections));

      dispatch({
        payload: {
          trendChartData: newTrendChartData,
        },
        type: ZipProjectActionsEnum.EditTimeslicePublishComment,
      });
    } catch (error) {
      dispatch({
        payload: { error: error },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onResetStatusBanners = (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean) => {
    if (state.project) {
      const updatedProject = state.project;
      updatedProject.hasUnprocessedProviderTerminations = hasUnprocessedTerminations;
      updatedProject.hasUnprocessedProjectionProviders = hasUnprocessedTargets;

      dispatch({
        payload: {
          project: updatedProject,
        },
        type: ZipProjectActionsEnum.ResetStatusBanners ,
      });
    }
  }

  //Implement back-end code to run the projection analysis on zip projects here
  const onProcessProviderChanges = async (reprocessFailure: boolean): Promise<void> => {
    if (state.project && !state.project.isRunning && !reprocessFailure) {
      dispatch({
        payload: {
          project: {
            ...state.project,
            isReadOnly: true,
            isRunning: true,
          },
        },
        type: ZipProjectActionsEnum.ProcessProviderChanges,
      });
    }
  }

  return (
    <ZipProjectContext.Provider
      value={{
        dispatch,
        getSearchParametersFromFilterSelections,
        onApplyFilters,
        onChangeChangedGapsFilter,
        onChangeCompareToRunConfiguration,
        onChangePerspectiveToZipClass,
        onChangePerspectiveToZipCode,
        onChangeProjectStatus,
        onChangeRunConfiguration,
        onChangeSelectedMemberships,
        onChangeTablePage,
        onClickSpecialtyGroupOrZipCode,
        onEditTimeslicePublishComment,
        onLoadPage,
        onProcessProviderChanges,
        onPublishTimeSlice,
        onResetFilters,
        onResetStatusBanners,
        onRunProject,
        onSortTable,
        onSubmitEditProject,
        state,
      }}>
      {props.children}
    </ZipProjectContext.Provider>
  )
};

export function withZipProjectContext<T>(Component: ComponentType<PropsWithChildren<T & ZipContextState>>): ComponentType<PropsWithChildren<T>> {
  class WithZipProjectContext extends PureComponent<T> {
    public render() {
      return (
        <ZipProjectContext.Consumer>
          {(context) => <Component {...this.props} {...context.state} />}
        </ZipProjectContext.Consumer>
      );
    }
  }

  return WithZipProjectContext;
}

export default ZipProjectContextProvider;